import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Navbar,
  Nav,
  Form,
  Button,
  InputGroup,
  Card,
  Spinner,
  Modal,
  Badge,
  Dropdown,
  DropdownButton,
  Alert,
} from "react-bootstrap";
import "./image-design-chat.css";

/**
 * ImageDesignChat
 * Chat-style UI for generating & editing jewelry designs
 * Backend Endpoints (DRF):
 *   POST /api/images/generate/
 *   POST /api/images/edit/
 */
export default function ImageDesignChat({ apiBase = "/api" }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [size, setSize] = useState("1024x1024");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showEdit, setShowEdit] = useState(false);
  const [editImageUrl, setEditImageUrl] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sizes = useMemo(() => ["512x512", "1024x1024", "2048x2048"], []);

  const handleSend = async () => {
    const prompt = input.trim();
    if (!prompt) return;
    setInput("");
    setError("");
    setMessages((m) => [...m, { role: "user", content: prompt }]);
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/images/generate/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, size, n: 1, save: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to generate image");
      const imgs = (data.results || []).map((r) => r.url || r.data_url);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `Generated for: ${data.prompt}`,
          images: imgs,
        },
      ]);
    } catch (e) {
      setError(e.message);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `⚠️ ${e.message}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (imageUrl) => {
    setEditImageUrl(imageUrl);
    setEditPrompt("");
    setEditError("");
    setShowEdit(true);
  };

  const onSubmitEdit = async (bbox) => {
    if (!editImageUrl || !bbox) return;
    setEditLoading(true);
    setEditError("");

    try {
      const form = new FormData();
      const blob = await fetch(editImageUrl).then((r) => r.blob());
      form.append("image", blob, "design.png");
      form.append("x", Math.round(bbox.x));
      form.append("y", Math.round(bbox.y));
      form.append("w", Math.round(bbox.w));
      form.append("h", Math.round(bbox.h));
      form.append("prompt", editPrompt);
      form.append("size", size);
      form.append("n", 1);

      const res = await fetch(`${apiBase}/images/edit/`, {
        method: "POST",
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Edit failed");

      const imgs = (data.results || []).map((r) => r.url || r.data_url);
      setMessages((m) => [
        ...m,
        { role: "user", content: `Edit: ${editPrompt}` },
        { role: "assistant", content: `Edited region`, images: imgs },
      ]);
      setShowEdit(false);
    } catch (e) {
      setEditError(e.message);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="idc-root">
      <Navbar bg="light" expand="md" className="shadow-sm idc-navbar">
        <Container>
          <Navbar.Brand className="fw-semibold">
            Jewelry Design Studio
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="idc-nav" />
          <Navbar.Collapse id="idc-nav">
            <Nav className="ms-auto align-items-center gap-2">
              <span className="text-muted small d-none d-md-inline">
                Output size
              </span>
              <DropdownButton
                variant="outline-secondary"
                title={size}
                align="end"
              >
                {sizes.map((s) => (
                  <Dropdown.Item
                    key={s}
                    active={s === size}
                    onClick={() => setSize(s)}
                  >
                    {s}
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container fluid="md" className="idc-container py-3">
        <Row className="g-3">
          <Col xs={12}>
            {error && <Alert variant="danger">{error}</Alert>}

            <Card className="idc-chat-card">
              <Card.Body className="idc-chat-body">
                {messages.length === 0 && (
                  <div className="idc-empty-state text-center text-muted">
                    <h5 className="fw-semibold mb-2">Start with a prompt</h5>
                    <p className="mb-0">
                      e.g. “A platinum engagement ring with a 2ct emerald-cut
                      center stone, micro-pavé band.”
                    </p>
                  </div>
                )}

                {messages.map((m, idx) => (
                  <div
                    key={idx}
                    className={`idc-msg ${
                      m.role === "user" ? "idc-msg-user" : "idc-msg-assistant"
                    }`}
                  >
                    <div className="idc-bubble">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <Badge bg={m.role === "user" ? "primary" : "secondary"}>
                          {m.role === "user" ? "You" : "Assistant"}
                        </Badge>
                        <span className="idc-bubble-title">{m.content}</span>
                      </div>

                      {Array.isArray(m.images) && m.images.length > 0 && (
                        <div className="idc-img-grid">
                          {m.images.map((src, i) => (
                            <Card key={i} className="idc-img-card">
                              <a
                                href={src}
                                target="_blank"
                                rel="noreferrer"
                                className="idc-img-link"
                              >
                                <img
                                  src={src}
                                  alt={`generated-${i}`}
                                  className="idc-img"
                                />
                              </a>
                              <Card.Footer className="p-2 text-end bg-white">
                                <Button
                                  size="sm"
                                  variant="outline-dark"
                                  onClick={() => openEditModal(src)}
                                >
                                  Edit this element
                                </Button>
                              </Card.Footer>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="idc-loading">
                    <Spinner animation="border" role="status" />
                  </div>
                )}

                <div ref={bottomRef} />
              </Card.Body>
              <Card.Footer className="bg-white">
                <InputGroup className="idc-input">
                  <Form.Control
                    placeholder="Describe a jewelry design to generate…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                  <Button
                    onClick={handleSend}
                    disabled={loading}
                    variant="dark"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />{" "}
                        Generating…
                      </>
                    ) : (
                      "Send"
                    )}
                  </Button>
                </InputGroup>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>

      <EditModal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        imageUrl={editImageUrl}
        prompt={editPrompt}
        setPrompt={setEditPrompt}
        onSubmit={onSubmitEdit}
        size={size}
        loading={editLoading}
        error={editError}
      />
    </div>
  );
}

function EditModal({
  show,
  onHide,
  imageUrl,
  prompt,
  setPrompt,
  onSubmit,
  size,
  loading,
  error,
}) {
  const [bbox, setBbox] = useState(null);
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit selected element</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Row className="g-3">
          <Col xs={12} md={7}>
            {imageUrl && <MaskBox src={imageUrl} onChange={setBbox} />}
          </Col>
          <Col xs={12} md={5}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Describe the change</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  placeholder="Replace the center stone with an emerald-cut vivid green emerald ~2ct."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </Form.Group>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="small text-muted">Output size: {size}</div>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setBbox(null)}
                    disabled={loading}
                  >
                    Reset box
                  </Button>
                  <Button
                    variant="dark"
                    onClick={() => bbox && onSubmit(bbox)}
                    disabled={!bbox || !prompt || loading}
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Apply edit"
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

function MaskBox({ src, onChange }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(new Image());
  const [loaded, setLoaded] = useState(false);
  const [start, setStart] = useState(null);
  const [rect, setRect] = useState(null);
  const [scale, setScale] = useState({ x: 1, y: 1 });

  useEffect(() => {
    const img = imgRef.current;
    img.crossOrigin = "anonymous";
    img.onload = () => setLoaded(true);
    img.src = src;
  }, [src]);

  useEffect(() => {
    if (!loaded) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const maxW = Math.min(800, canvas.parentElement.clientWidth);
    const ratio = imgRef.current.width / imgRef.current.height;
    const cw = Math.round(Math.min(maxW, imgRef.current.width));
    const ch = Math.round(cw / ratio);
    canvas.width = cw;
    canvas.height = ch;
    setScale({ x: imgRef.current.width / cw, y: imgRef.current.height / ch });
    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(imgRef.current, 0, 0, cw, ch);
    if (rect) {
      ctx.fillStyle = "rgba(0,0,0,0.25)";
      ctx.fillRect(0, 0, cw, ch);
      ctx.clearRect(rect.x, rect.y, rect.w, rect.h);
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#222";
      ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
    }
  }, [loaded, rect]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e) => setStart(pos(e));
  const move = (e) => {
    if (!start) return;
    const p = pos(e);
    const x = Math.min(start.x, p.x);
    const y = Math.min(start.y, p.y);
    const w = Math.abs(p.x - start.x);
    const h = Math.abs(p.y - start.y);
    setRect({ x, y, w, h });
  };
  const up = () => {
    setStart(null);
    if (rect)
      onChange({
        x: Math.round(rect.x * scale.x),
        y: Math.round(rect.y * scale.y),
        w: Math.round(rect.w * scale.x),
        h: Math.round(rect.h * scale.y),
      });
  };

  return (
    <div className="idc-maskbox">
      <canvas
        ref={canvasRef}
        className="idc-maskbox-canvas"
        onMouseDown={down}
        onMouseMove={move}
        onMouseUp={up}
      />
      {!rect && (
        <div className="small text-muted mt-2">Drag to select area</div>
      )}
    </div>
  );
}
